/**
 * BarriersCo Professional Mega Menu v2.0
 * Amazon-style navigation with product fetching and mobile drawer
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    hoverDelay: 150,
    closeDelay: 200,
    productCount: 3,
    cacheExpiry: 5 * 60 * 1000, // 5 minutes
  };

  // State
  let hoverTimeout = null;
  let closeTimeout = null;
  let activeItem = null;
  let productCache = {};
  let mobileMenuOpen = false;

  // DOM Elements
  const elements = {
    menu: null,
    mobileMenu: null,
    mobileToggle: null,
    industryPanel: null,
    industryToggle: null,
  };

  /**
   * Initialize the mega menu
   */
  function init() {
    elements.menu = document.getElementById('mega-menu-pro');
    elements.mobileMenu = document.getElementById('mobile-menu-pro');
    elements.mobileToggle = document.querySelector('[data-mobile-toggle]');
    elements.industryPanel = document.querySelector('[data-industry-panel]');
    elements.industryToggle = document.querySelector('[data-industry-toggle]');

    if (!elements.menu) return;

    setupDesktopMenu();
    setupMobileMenu();
    setupIndustryPanel();
    setupKeyboardNavigation();
    loadProductsForVisibleDropdowns();
  }

  /**
   * Desktop Menu Hover Logic
   */
  function setupDesktopMenu() {
    const menuItems = elements.menu.querySelectorAll('[data-menu-item]');

    menuItems.forEach(item => {
      const trigger = item.querySelector('[data-trigger]');
      const dropdown = item.querySelector('[data-dropdown]');

      if (!trigger || !dropdown) return;

      // Mouse enter
      item.addEventListener('mouseenter', () => {
        clearTimeout(closeTimeout);
        
        hoverTimeout = setTimeout(() => {
          openDropdown(item);
        }, CONFIG.hoverDelay);
      });

      // Mouse leave
      item.addEventListener('mouseleave', () => {
        clearTimeout(hoverTimeout);
        
        closeTimeout = setTimeout(() => {
          closeDropdown(item);
        }, CONFIG.closeDelay);
      });

      // Click (for mobile/touch)
      trigger.addEventListener('click', (e) => {
        if (window.innerWidth <= 999) return;
        
        if (item.classList.contains('is-active')) {
          e.preventDefault();
          closeDropdown(item);
        } else {
          e.preventDefault();
          closeAllDropdowns();
          openDropdown(item);
        }
      });
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-menu-item]')) {
        closeAllDropdowns();
      }
    });
  }

  /**
   * Open a dropdown
   */
  function openDropdown(item) {
    if (activeItem && activeItem !== item) {
      closeDropdown(activeItem);
    }

    item.classList.add('is-active');
    const dropdown = item.querySelector('[data-dropdown]');
    if (dropdown) {
      dropdown.setAttribute('aria-hidden', 'false');
    }

    activeItem = item;

    // Load products for this dropdown
    const productsContainer = item.querySelector('[data-products]');
    if (productsContainer) {
      const collection = productsContainer.dataset.products;
      loadProducts(collection, productsContainer);
    }
  }

  /**
   * Close a dropdown
   */
  function closeDropdown(item) {
    item.classList.remove('is-active');
    const dropdown = item.querySelector('[data-dropdown]');
    if (dropdown) {
      dropdown.setAttribute('aria-hidden', 'true');
    }

    if (activeItem === item) {
      activeItem = null;
    }
  }

  /**
   * Close all dropdowns
   */
  function closeAllDropdowns() {
    const items = elements.menu.querySelectorAll('[data-menu-item]');
    items.forEach(item => closeDropdown(item));
  }

  /**
   * Load products for a collection
   */
  async function loadProducts(collection, container) {
    if (!collection || !container) return;

    // Check cache
    const cacheKey = collection;
    const cached = productCache[cacheKey];
    if (cached && Date.now() - cached.timestamp < CONFIG.cacheExpiry) {
      renderProducts(cached.products, container);
      return;
    }

    // Show loading state
    container.innerHTML = `
      <div class="mmenu__products-loading">
        ${Array(CONFIG.productCount).fill('').map(() => `
          <div class="mmenu__product-skeleton">
            <div class="mmenu__skeleton-image"></div>
            <div class="mmenu__skeleton-text">
              <div class="mmenu__skeleton-line"></div>
              <div class="mmenu__skeleton-line"></div>
            </div>
          </div>
        `).join('')}
      </div>
    `;

    try {
      // Fetch products from collection
      const response = await fetch(`/collections/${collection}/products.json?limit=${CONFIG.productCount}`);
      if (!response.ok) throw new Error('Failed to fetch products');

      const data = await response.json();
      const products = data.products || [];

      // Cache the results
      productCache[cacheKey] = {
        timestamp: Date.now(),
        products: products,
      };

      renderProducts(products, container);
    } catch (error) {
      console.error('Error loading products:', error);
      container.innerHTML = '<p style="color: #888; font-size: 12px;">Products unavailable</p>';
    }
  }

  /**
   * Render products in container
   */
  function renderProducts(products, container) {
    if (!products || products.length === 0) {
      container.innerHTML = '<p style="color: #888; font-size: 12px;">No products found</p>';
      return;
    }

    const html = products.slice(0, CONFIG.productCount).map(product => {
      const image = product.images && product.images[0] 
        ? product.images[0].src.replace(/\.([^\.]+)$/, '_180x180.$1')
        : 'https://via.placeholder.com/60x60/222/666?text=No+Image';

      const price = product.variants && product.variants[0] 
        ? formatPrice(product.variants[0].price)
        : '';

      const title = truncateText(product.title, 40);

      return `
        <a href="/products/${product.handle}" class="mmenu__product">
          <img 
            src="${image}" 
            alt="${escapeHtml(product.title)}" 
            class="mmenu__product-image"
            loading="lazy"
            width="60"
            height="60"
          >
          <div class="mmenu__product-info">
            <div class="mmenu__product-title">${escapeHtml(title)}</div>
            ${price ? `<div class="mmenu__product-price">${price}</div>` : ''}
          </div>
        </a>
      `;
    }).join('');

    container.innerHTML = html;
  }

  /**
   * Load products for visible dropdowns (preload)
   */
  function loadProductsForVisibleDropdowns() {
    // Preload first 3 collections
    const containers = elements.menu.querySelectorAll('[data-products]');
    const collectionsToPreload = [];
    
    containers.forEach((container, index) => {
      if (index < 3) {
        collectionsToPreload.push(container.dataset.products);
      }
    });

    // Stagger the requests
    collectionsToPreload.forEach((collection, index) => {
      setTimeout(() => {
        const container = elements.menu.querySelector(`[data-products="${collection}"]`);
        if (container) {
          loadProducts(collection, container);
        }
      }, index * 500);
    });
  }

  /**
   * Mobile Menu Setup
   */
  function setupMobileMenu() {
    if (!elements.mobileMenu || !elements.mobileToggle) return;

    // Toggle button
    elements.mobileToggle.addEventListener('click', () => {
      if (mobileMenuOpen) {
        closeMobileMenu();
      } else {
        openMobileMenu();
      }
    });

    // Close buttons
    const closeButtons = elements.mobileMenu.querySelectorAll('[data-mobile-close]');
    closeButtons.forEach(btn => {
      btn.addEventListener('click', closeMobileMenu);
    });

    // Accordion triggers
    const accordionTriggers = elements.mobileMenu.querySelectorAll('[data-mobile-trigger]');
    accordionTriggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        const item = trigger.closest('[data-mobile-item]');
        const isOpen = item.classList.contains('is-open');

        // Close other accordions (optional - remove for multi-open)
        // const allItems = elements.mobileMenu.querySelectorAll('[data-mobile-item]');
        // allItems.forEach(i => i.classList.remove('is-open'));

        if (isOpen) {
          item.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
        } else {
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        closeMobileMenu();
      }
    });
  }

  /**
   * Open mobile menu
   */
  function openMobileMenu() {
    mobileMenuOpen = true;
    elements.mobileMenu.setAttribute('aria-hidden', 'false');
    elements.mobileToggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';

    // Focus first link
    setTimeout(() => {
      const firstFocusable = elements.mobileMenu.querySelector('button, a, input');
      if (firstFocusable) firstFocusable.focus();
    }, 100);
  }

  /**
   * Close mobile menu
   */
  function closeMobileMenu() {
    mobileMenuOpen = false;
    elements.mobileMenu.setAttribute('aria-hidden', 'true');
    elements.mobileToggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    elements.mobileToggle.focus();
  }

  /**
   * Industry Panel Setup
   */
  function setupIndustryPanel() {
    if (!elements.industryPanel || !elements.industryToggle) return;

    elements.industryToggle.addEventListener('click', (e) => {
      e.stopPropagation();
      const isActive = elements.industryPanel.classList.contains('is-active');

      if (isActive) {
        elements.industryPanel.classList.remove('is-active');
        elements.industryToggle.setAttribute('aria-expanded', 'false');
      } else {
        elements.industryPanel.classList.add('is-active');
        elements.industryToggle.setAttribute('aria-expanded', 'true');
      }
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.mmenu__industry')) {
        elements.industryPanel.classList.remove('is-active');
        elements.industryToggle.setAttribute('aria-expanded', 'false');
      }
    });
  }

  /**
   * Keyboard Navigation
   */
  function setupKeyboardNavigation() {
    if (!elements.menu) return;

    const menuItems = elements.menu.querySelectorAll('[data-menu-item]');
    const triggers = Array.from(elements.menu.querySelectorAll('[data-trigger]'));

    elements.menu.addEventListener('keydown', (e) => {
      const currentTrigger = document.activeElement.closest('[data-trigger]');
      if (!currentTrigger) return;

      const currentIndex = triggers.indexOf(currentTrigger);
      let newIndex;

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          newIndex = (currentIndex + 1) % triggers.length;
          triggers[newIndex].focus();
          break;

        case 'ArrowLeft':
          e.preventDefault();
          newIndex = (currentIndex - 1 + triggers.length) % triggers.length;
          triggers[newIndex].focus();
          break;

        case 'ArrowDown':
          e.preventDefault();
          const item = currentTrigger.closest('[data-menu-item]');
          openDropdown(item);
          const firstLink = item.querySelector('.mmenu__links a, .mmenu__product');
          if (firstLink) firstLink.focus();
          break;

        case 'Escape':
          closeAllDropdowns();
          currentTrigger.focus();
          break;

        case 'Enter':
        case ' ':
          e.preventDefault();
          const parentItem = currentTrigger.closest('[data-menu-item]');
          if (parentItem.classList.contains('is-active')) {
            closeDropdown(parentItem);
          } else {
            openDropdown(parentItem);
          }
          break;
      }
    });

    // Arrow navigation within dropdowns
    menuItems.forEach(item => {
      const dropdown = item.querySelector('[data-dropdown]');
      if (!dropdown) return;

      dropdown.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          closeDropdown(item);
          item.querySelector('[data-trigger]').focus();
        }

        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          const links = Array.from(dropdown.querySelectorAll('a'));
          const currentIndex = links.indexOf(document.activeElement);

          if (e.key === 'ArrowDown') {
            const nextIndex = (currentIndex + 1) % links.length;
            links[nextIndex].focus();
          } else {
            const prevIndex = (currentIndex - 1 + links.length) % links.length;
            links[prevIndex].focus();
          }
        }
      });
    });
  }

  /**
   * Utility: Format price
   */
  function formatPrice(price) {
    const numPrice = parseFloat(price);
    if (isNaN(numPrice)) return '';
    return '£' + numPrice.toFixed(2);
  }

  /**
   * Utility: Truncate text
   */
  function truncateText(text, maxLength) {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  }

  /**
   * Utility: Escape HTML
   */
  function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on Shopify section reload
  document.addEventListener('shopify:section:load', init);

})();
